import { useSyncExternalStore } from 'react';
import { api } from './api';
import { tokenStore } from './token-store';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  handle: string;
  breakerId: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: string;
  refreshTokenExpiresAt: string;
}

export interface AuthResponse {
  user: AuthUser;
  tokens: AuthTokens;
}

/** Subscribe a component to auth state changes (token store updates). */
export function useAuthState(): {
  isAuthenticated: boolean;
  hasValidAccess: boolean;
} {
  const subscribe = (cb: () => void) => tokenStore.subscribe(cb);
  const snapshot = () =>
    `${tokenStore.isAuthenticated()}:${tokenStore.hasValidAccess()}`;
  useSyncExternalStore(subscribe, snapshot, snapshot);
  return {
    isAuthenticated: tokenStore.isAuthenticated(),
    hasValidAccess: tokenStore.hasValidAccess(),
  };
}

export const authService = {
  async login(email: string, password: string): Promise<AuthResponse> {
    const res = await api<AuthResponse>('/v1/auth/login', {
      method: 'POST',
      body: { email, password },
      unauthenticated: true,
    });
    tokenStore.setAccess(res.tokens.accessToken, res.tokens.accessTokenExpiresAt);
    tokenStore.setRefresh(res.tokens.refreshToken);
    return res;
  },

  // ---- P1: email-code sign-in (PLAN Q1 — passwordless owner principals).
  // Uses the plain OTP rail, NOT /signup/* — login must never pollute the
  // signup funnel events.
  async otpRequest(email: string): Promise<void> {
    await api<void>('/v1/auth/otp/request', {
      method: 'POST',
      body: { email },
      unauthenticated: true,
    });
  },

  /** signed_in → tokens stored; signup → this email has no account. */
  async otpVerify(
    email: string,
    code: string,
  ): Promise<'signed_in' | 'no_account'> {
    const res = await api<
      | { status: 'signed_in'; user: AuthUser; tokens: AuthTokens }
      | { status: 'signup'; signupToken: string }
    >('/v1/auth/otp/verify', {
      method: 'POST',
      body: { email, code },
      unauthenticated: true,
    });
    if (res.status !== 'signed_in') return 'no_account';
    tokenStore.setAccess(res.tokens.accessToken, res.tokens.accessTokenExpiresAt);
    tokenStore.setRefresh(res.tokens.refreshToken);
    return 'signed_in';
  },

  // ---- P1 signup rail (/signup page) ----
  async signupStart(args: {
    name: string;
    email: string;
    companyName: string;
    source?: string;
  }): Promise<void> {
    await api<void>('/v1/auth/signup/start', {
      method: 'POST',
      body: args,
      unauthenticated: true,
    });
  },

  /**
   * Funnel step beacon (funnel-emails unit). Fire-and-forget: the server
   * 204s regardless and a dropped row costs a digest number, so this must
   * never surface an error into the wizard.
   */
  signupStep(args: { step: string; email?: string; source?: string }): void {
    void api<void>('/v1/auth/signup/step', {
      method: 'POST',
      body: args,
      unauthenticated: true,
    }).catch(() => undefined);
  },

  async signupVerify(
    email: string,
    code: string,
  ): Promise<{ status: 'signed_in' } | { status: 'signup'; signupToken: string }> {
    const res = await api<
      | { status: 'signed_in'; user: AuthUser; tokens: AuthTokens }
      | { status: 'signup'; signupToken: string }
    >('/v1/auth/signup/verify', {
      method: 'POST',
      body: { email, code },
      unauthenticated: true,
    });
    if (res.status === 'signed_in') {
      tokenStore.setAccess(res.tokens.accessToken, res.tokens.accessTokenExpiresAt);
      tokenStore.setRefresh(res.tokens.refreshToken);
      return { status: 'signed_in' };
    }
    return res;
  },

  async signupComplete(args: {
    signupToken: string;
    name: string;
    companyName: string;
    slug: string;
    /** 0084: optional comp code. Redemption is decided server-side inside
     *  the provisioning transaction — the blur-check was only advice. */
    compCode?: string;
    /** Step-3 address default telemetry (address-default unit): what was
     *  pre-selected, what was submitted, and whether the pre-filled slug
     *  was hand-edited. Counted only — provisioning is identical either
     *  way. */
    defaultDoor?: 'free' | 'buy' | 'byo';
    chosenDoor?: 'free' | 'buy' | 'byo';
    slugEdited?: boolean;
  }): Promise<{
    portalUrl: string;
    fleetId: string;
    isTest: boolean;
    comp: { requested: boolean; applied: boolean };
  }> {
    const res = await api<
      AuthResponse & {
        portalUrl: string;
        fleet: { id: string; isTest: boolean };
        comp: { requested: boolean; applied: boolean };
      }
    >('/v1/auth/signup/complete', { method: 'POST', body: args, unauthenticated: true });
    tokenStore.setAccess(res.tokens.accessToken, res.tokens.accessTokenExpiresAt);
    tokenStore.setRefresh(res.tokens.refreshToken);
    // Setup wizard: steps 4–5 run authenticated against this fleet; isTest
    // decides whether the in-wizard purchase option is even reachable.
    return {
      portalUrl: res.portalUrl,
      fleetId: res.fleet.id,
      isTest: res.fleet.isTest,
      comp: res.comp,
    };
  },

  async signupConfig(): Promise<{
    domainPurchase: boolean;
    tlds: Array<{ tld: string; retailPence: number }>;
    testOverride: boolean;
  }> {
    return api('/v1/auth/signup/config', { unauthenticated: true });
  },

  /**
   * 0084 — the wizard's on-blur comp-code check. One shape for every
   * outcome; a network failure resolves to "not valid" rather than
   * throwing, because this is advice and must never block the step.
   */
  /** POST with the code in the BODY: a query string would be written to the
   *  api access log, the pm2 log and then http_requests. See the route.
   *  The email rides the body for the same reason — it is PII, and an
   *  access log is not where it belongs. */
  async compCodeCheck(code: string, email?: string): Promise<{ valid: boolean }> {
    return api<{ valid: boolean }>('/v1/auth/signup/comp-check', {
      method: 'POST',
      // email is passed only so an invalid attempt can be correlated to a
      // signup in the digest — it never changes the answer.
      body: { code, ...(email ? { email } : {}) },
      unauthenticated: true,
    });
  },

  async slugCheck(
    slug: string,
  ): Promise<{ available: boolean; reason?: 'reserved' | 'taken' }> {
    return api<{ available: boolean; reason?: 'reserved' | 'taken' }>(
      '/v1/auth/signup/slug-check',
      { query: { slug }, unauthenticated: true },
    );
  },

  async logout(): Promise<void> {
    const refresh = tokenStore.getRefresh();
    try {
      if (refresh) {
        await api('/v1/auth/logout', {
          method: 'POST',
          body: { refreshToken: refresh },
        });
      }
    } catch {
      // Best-effort. Always clear local state regardless of server response.
    }
    tokenStore.clear();
  },
};
